'use client';

import { useEffect, useRef, useState } from 'react';
import { faBars } from '@fortawesome/pro-solid-svg-icons/faBars';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { Icon } from '@benzinga/core-ui';
import type { Ticker } from '@benzinga/session';
import type { TrendingTopic } from '@benzinga/trending-topics-manager';
import type { Section, SectionLink, PageContext } from '../../entities';
import type { PremiumServiceAccess, PremiumServiceKey } from '../../premiumServiceLinks';
import { sectionMenus as defaultSectionMenus } from '../../section-menu-data';
import { SectionMenuButton } from './SectionMenuButton';
import { SectionMenu } from './SectionMenu';
import { NavLogo } from './NavLogo';
import { PremiumServicesLink } from './PremiumServicesLink';
import { TickerSearch } from './TickerSearch';
import styles from './SiteNav.module.scss';

export interface SectionNavProps {
  sections?: Section[];
  variant?: 'default' | 'dark';
  premiumAccess: PremiumServiceAccess;
  pageContext?: PageContext;
  initialTrendingTickers?: Ticker[];
  initialTrendingTopics?: TrendingTopic[];
  onSectionOpen?: (section: Section) => void;
  onSectionLinkClick?: (section: Section, link: SectionLink) => void;
  onSectionTitleClick?: (section: Section) => void;
  onPremiumClick?: (service: PremiumServiceKey) => void;
  hideTickerSearch?: boolean;
  hidePremium?: boolean;
  mobileMenuOpen?: boolean;
  onOpenMobileMenu?: () => void;
  onSearchActivate?: () => void;
}

export const SectionNav = ({
  sections = defaultSectionMenus,
  variant = 'default',
  premiumAccess,
  pageContext,
  initialTrendingTickers,
  initialTrendingTopics,
  mobileMenuOpen,
  onSectionOpen,
  onSectionLinkClick,
  onSectionTitleClick,
  onPremiumClick,
  hideTickerSearch = false,
  hidePremium = false,
  onOpenMobileMenu,
  onSearchActivate,
}: SectionNavProps) => {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeSection = openKey ? sections.find(section => section.key === openKey) ?? null : null;
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const cancelOpen = () => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
      if (openTimer.current) clearTimeout(openTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    document.getElementById('searchbar-input')?.blur();
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!openKey) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenKey(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openKey]);

  const closeSearchDropdown = () => {
    document.getElementById('searchbar-input')?.blur();
  };

  const handleSearchActivate = () => {
    cancelOpen();
    cancelClose();
    setOpenKey(null);
    onSearchActivate?.();
  };

  const handleOpen = (section: Section) => {
    cancelClose();
    cancelOpen();
    const open = () => {
      closeSearchDropdown();
      setOpenKey(section.key);
      onSectionOpen?.(section);
    };
    if (openKey) open();
    else openTimer.current = setTimeout(open, 120);
  };
  const closeSections = () => {
    cancelOpen();
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenKey(null), 150);
  };

  return (
    <div className="relative border-b border-[var(--hubnav-line)]" onMouseLeave={closeSections}>
      <div className={`${styles.bar} mx-auto flex w-full max-w-[var(--hubnav-max-width)] items-center px-[var(--hubnav-gutter)]`}>
        <button
          className={styles.menuButton}
          type="button"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
          onClick={onOpenMobileMenu}
        >
          <Icon className="h-[18px] w-[18px]" icon={mobileMenuOpen ? faXmark : faBars} />
        </button>

        <span className={styles.logoSlot} onMouseEnter={closeSections}>
          <NavLogo variant={variant} />
        </span>

        {!hidePremium ? (
          <span className="ml-3 inline-flex flex-none max-[1080px]:hidden" onMouseEnter={closeSections}>
            <PremiumServicesLink
              access={premiumAccess}
              label={
                <>
                  <span className="max-[1400px]:hidden">Premium Services</span>
                  <span className="hidden max-[1400px]:inline">Services</span>
                </>
              }
              onLinkClick={onPremiumClick}
            />
          </span>
        ) : null}

        <nav className="flex items-center self-stretch max-[1080px]:hidden" aria-label="Primary">
          {sections.map(section => (
            <SectionMenuButton
              key={section.key}
              section={section}
              open={section.key === openKey}
              onOpen={() => handleOpen(section)}
              onClick={() => onSectionTitleClick?.(section)}
            />
          ))}
        </nav>

        {!hideTickerSearch ? (
          <div className={styles.tools} onMouseEnter={closeSections}>
            <TickerSearch
              dropdownSuppressed={!!openKey || !!mobileMenuOpen}
              initialTrendingTickers={initialTrendingTickers}
              initialTrendingTopics={initialTrendingTopics}
              onActivate={handleSearchActivate}
            />
          </div>
        ) : null}

        {activeSection ? (
          <div
            className={`${styles.panelIn} absolute left-[var(--hubnav-gutter)] top-[calc(100%+8px)] z-[60] w-max max-w-[calc(100%-2*var(--hubnav-gutter))] overflow-hidden rounded-xl bg-[var(--hubnav-panel-bg)] text-[var(--hubnav-fg)] before:absolute before:inset-x-0 before:-top-3 before:h-3 before:content-['']`}
            role="region"
            aria-label={activeSection.label}
            onMouseEnter={cancelClose}
          >
            <SectionMenu section={activeSection} pageContext={pageContext} onLinkClick={onSectionLinkClick} />
          </div>
        ) : null}
      </div>
    </div>
  );
};
